/**
 * Central API client. All HTTP calls go through here.
 * Base path is /api — in dev Vite proxies to localhost:8000, in prod nginx proxies.
 */
import type {
  Chat,
  Message,
  CanvasWorkspace,
  CanvasRevision,
  Project,
  ProjectFile,
  WikiPage,
  WikiIssue,
  Memory,
  ResearchRun,
  MCPServer,
  MCPCatalogEntry,
  MCPTool,
  GeneratedImage,
  Plugin,
  Skill,
  ModelsByProvider,
  LLMSettings,
  SearchResult,
  SearchSettings,
  SearchProviderName,
  SettingsMode,
  SearchThread,
  SearchThreadsResponse,
  SSEEvent,
  AnswerSSEEvent,
  CanvasMode,
  HereNowConnection,
  PublishTarget,
  SitePublication,
  PublishRequest,
} from "../types";
import { parseAnswerStream } from "../lib/sseClient";
import * as debugCapture from "../lib/debugCapture";

const BASE = "/api";

class ApiError extends Error {
  status: number;
  // Task 9.2 — a pydantic 422's `detail` is an array of
  // {loc, msg, type} entries, not a string. `message` below stays a
  // human-readable joined summary (for anything that just does
  // `e.message`); `detail` preserves the raw array so a view can render
  // it structurally instead of one flattened alert/toast string.
  detail?: unknown;
  constructor(message: string, status: number, detail?: unknown) {
    super(message);
    this.status = status;
    this.name = "ApiError";
    this.detail = detail;
  }
}

/** FastAPI/pydantic validation errors: `detail` is `[{loc, msg, type}, ...]`. */
export interface ValidationErrorEntry {
  loc: (string | number)[];
  msg: string;
  type: string;
}

/** Fired on any 401 from authedFetch — main.tsx's route guard listens for
 * this to bounce to /login. A plain redirect from inside this module would
 * fight React Router's own navigation and reload the whole app. */
export const AUTH_EVENT = "chatclient:unauthorized";

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/**
 * The one place a network request is allowed to leave this module (Task
 * 3.3 — "the two streaming call sites bypass request() and will be
 * missed"). Every fetch, streaming or not, goes through here: credentials
 * are always sent so the httpOnly session cookie rides along, and the
 * double-submit CSRF header is attached on every mutating method. A bare
 * call to the global fetch anywhere else in this file is a bug.
 *
 * Phase D1/D5 — every call gets a fresh `X-Request-ID` (unless the caller
 * already set one, e.g. an SSE call site that needs the same id to tag its
 * frames — see sendMessage/decision/answerApi.stream below) and is
 * mirrored into debugCapture's network ring buffer, a no-op unless capture
 * is enabled.
 */
async function authedFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const method = (init.method || "GET").toUpperCase();
  const headers: Record<string, string> = { ...(init.headers as Record<string, string>) };
  if (UNSAFE_METHODS.has(method)) {
    const csrf = readCookie("csrf_token");
    if (csrf) headers["X-CSRF-Token"] = csrf;
  }
  if (!headers["X-Request-ID"]) headers["X-Request-ID"] = debugCapture.newRequestId();
  const requestId = headers["X-Request-ID"];
  const start = performance.now();
  let res: Response;
  try {
    res = await fetch(url, { ...init, credentials: "include", headers });
  } catch (err) {
    debugCapture.recordNetwork({
      id: requestId, method, url, request_id: requestId, ts: Date.now(),
      duration_ms: performance.now() - start, ok: false, error: (err as Error).message,
    });
    throw err;
  }
  debugCapture.recordNetwork({
    id: requestId, method, url, status: res.status, ok: res.ok, request_id: requestId,
    ts: Date.now(), duration_ms: performance.now() - start,
  });
  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent(AUTH_EVENT));
  }
  return res;
}

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const res = await authedFetch(`${BASE}${path}`, {
    ...options,
    headers: {
      ...(options.headers || {}),
      ...(options.body instanceof FormData
        ? {}
        : { "Content-Type": "application/json" }),
    },
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    let detail: unknown;
    try {
      const err = await res.json();
      detail = err.detail;
      if (Array.isArray(err.detail)) {
        // pydantic validation error array — join into one readable line
        // for callers that only look at `.message`; `.detail` keeps the
        // structured array for a caller that wants to render it as a list.
        msg = err.detail
          .map((d: { loc?: unknown[]; msg?: string }) =>
            d.msg ? `${(d.loc || []).join(".")}: ${d.msg}` : JSON.stringify(d)
          )
          .join("; ") || msg;
      } else {
        msg = err.detail || err.message || msg;
      }
    } catch {
      // ignore
    }
    throw new ApiError(msg, res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) return (await res.json()) as T;
  return (await res.text()) as unknown as T;
}

function qs(params: Record<string, string | number | boolean | undefined>) {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null) sp.append(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

// ---------- Chat ----------

/**
 * Chat-list change events. The sidebar keeps its list current by applying
 * these one-row deltas instead of refetching /api/chats — every mutation
 * already flows through this module, so this is the natural chokepoint.
 * (The sidebar used to refetch the full list on every route change, which
 * read as "the chat list reloads constantly" and re-rendered every row.)
 */
export const CHATS_CHANGED_EVENT = "chatclient:chats-changed";
export type ChatsChangedDetail =
  | { type: "upsert"; chat: Chat }
  | { type: "title"; id: string; title: string }
  | { type: "remove"; id: string };

function emitChatsChanged(detail: ChatsChangedDetail) {
  window.dispatchEvent(new CustomEvent<ChatsChangedDetail>(CHATS_CHANGED_EVENT, { detail }));
}

export const chatApi = {
  list: (project_id?: string, limit = 100, offset = 0) =>
    request<Chat[]>(`/chats${qs({ project_id, limit, offset })}`),
  get: (id: string) => request<Chat>(`/chats/${id}`),
  create: async (data: { title?: string; project_id?: string; model_override?: string | null }) => {
    const chat = await request<Chat>(`/chats`, { method: "POST", body: JSON.stringify(data) });
    emitChatsChanged({ type: "upsert", chat });
    return chat;
  },
  update: async (id: string, data: { title?: string; model_override?: string | null }) => {
    const chat = await request<Chat>(`/chats/${id}`, { method: "PUT", body: JSON.stringify(data) });
    emitChatsChanged({ type: "upsert", chat });
    return chat;
  },
  remove: async (id: string) => {
    await request<void>(`/chats/${id}`, { method: "DELETE" });
    emitChatsChanged({ type: "remove", id });
  },
  messages: (id: string) => request<Message[]>(`/chats/${id}/messages`),
  /**
   * Send a message and stream SSE events. Uses fetch + ReadableStream because
   * the endpoint is POST (EventSource only supports GET).
   */
  /**
   * Send a message and stream SSE events. Uses fetch + ReadableStream
   * because the endpoint is POST (EventSource only supports GET). Parsing
   * is delegated to lib/sseClient.ts's pure `parseAnswerStream` (Task
   * 5.12) — `signal` both aborts the fetch itself and is passed through so
   * the reader is explicitly cancelled too (F8-1: Stop must actually stop
   * consumption, not just ignore further events).
   */
  sendMessage: async function* (
    id: string,
    body: {
      content: string;
      images?: string[];
      active_skills?: string[];
      use_memory?: boolean;
      use_tools?: boolean;
      // search-overhaul spec §7 — decoupled from use_tools: when true (the
      // composer's default) and intent isn't classified "none", the chat
      // turn runs the search pipeline and injects a cited sources block.
      web_search?: boolean;
      canvas_mode?: CanvasMode;
      active_workspace_id?: string;
      // Phase M — the client-side "auto-save memories" preference
      // (SettingsView, localStorage). The backend ANDs this with
      // use_memory before firing background extraction.
      auto_extract?: boolean;
      // Composer Thinking dropdown — only sent for models whose catalog
      // entry lists it; the backend re-validates and drops it otherwise.
      reasoning_effort?: string;
      // Settings "Skip tool confirmations" — runs Confirm-tier tool calls
      // without a suspension for this turn.
      skip_tool_confirmations?: boolean;
    },
    signal?: AbortSignal
  ): AsyncGenerator<SSEEvent> {
    const requestId = debugCapture.newRequestId();
    const res = await authedFetch(`${BASE}/chats/${id}/messages`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Request-ID": requestId },
      body: JSON.stringify(body),
      signal,
    });
    if (!res.ok || !res.body) {
      let msg = `HTTP ${res.status}`;
      try {
        const err = await res.json();
        msg = err.detail || msg;
      } catch {
        // ignore
      }
      const evt: SSEEvent = { type: "error", content: msg };
      debugCapture.recordSSEFrame(requestId, evt, id);
      yield evt;
      return;
    }
    for await (const evt of parseAnswerStream<SSEEvent>(res.body, signal)) {
      debugCapture.recordSSEFrame(requestId, evt, id);
      yield evt;
    }
  },
  autoTitle: async (id: string) => {
    const result = await request<{ title?: string }>(`/chats/${id}/auto-title`, { method: "POST" });
    if (result.title) emitChatsChanged({ type: "title", id, title: result.title });
    return result;
  },
  /**
   * Task 9.3 — resume a turn suspended on a Confirm-tier tool call
   * (`tool_confirm_required` + `done.pending_confirmation`). Same event
   * shapes as sendMessage; `{approve:false}` appends a "Denied by user."
   * tool message and the model continues without executing the tool.
   */
  decision: async function* (
    chatId: string,
    toolCallId: string,
    approve: boolean,
    signal?: AbortSignal,
    useMemory?: boolean,
    autoExtract?: boolean,
    skipToolConfirmations?: boolean
  ): AsyncGenerator<SSEEvent> {
    const requestId = debugCapture.newRequestId();
    const res = await authedFetch(
      `${BASE}/chats/${chatId}/tool-calls/${toolCallId}/decision`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Request-ID": requestId },
        body: JSON.stringify({
          approve,
          ...(useMemory !== undefined ? { use_memory: useMemory } : {}),
          ...(autoExtract !== undefined ? { auto_extract: autoExtract } : {}),
          ...(skipToolConfirmations !== undefined
            ? { skip_tool_confirmations: skipToolConfirmations }
            : {}),
        }),
        signal,
      }
    );
    if (!res.ok || !res.body) {
      let msg = `HTTP ${res.status}`;
      try {
        const err = await res.json();
        msg = err.detail || msg;
      } catch {
        // ignore
      }
      const evt: SSEEvent = { type: "error", content: msg };
      debugCapture.recordSSEFrame(requestId, evt, chatId);
      yield evt;
      return;
    }
    for await (const evt of parseAnswerStream<SSEEvent>(res.body, signal)) {
      debugCapture.recordSSEFrame(requestId, evt, chatId);
      yield evt;
    }
  },
};

export const canvasApi = {
  list: (chatId: string) => request<CanvasWorkspace[]>(`/chats/${chatId}/canvases`),
  create: (chatId: string, data: { title?: string; framework?: string; entry_path?: string | null }) => request<CanvasWorkspace>(`/chats/${chatId}/canvases`, { method: "POST", body: JSON.stringify(data) }),
  revisions: (id: string) => request<CanvasRevision[]>(`/canvases/${id}/revisions`),
  revision: (id: string, revisionId: string) => request<CanvasRevision>(`/canvases/${id}/revisions/${revisionId}`),
  file: (id: string, revisionId: string, path: string) => request<{ path: string; sha256: string; content: string }>(`/canvases/${id}/revisions/${revisionId}/file${qs({ path })}`),
  save: (id: string, body: { base_revision_id: string | null; summary: string; changes: object[] }) => request<{ workspace: CanvasWorkspace; revision: CanvasRevision; changed_paths: string[] }>(`/canvases/${id}/revisions`, { method: "POST", body: JSON.stringify(body) }),
  restore: (id: string, revision_id: string) => request<{ workspace: CanvasWorkspace; revision: CanvasRevision }>(`/canvases/${id}/restore?${qs({ revision_id })}`, { method: "POST" }),
  /** Mint a short-lived URL on the sandboxed preview origin (see
   * canvas_preview_service.py — that origin never receives our cookies, so
   * the token in the returned path is the only thing authorizing it). */
  previewToken: (id: string, revisionId: string) =>
    request<{ url: string; expires_in: number }>(`/canvases/${id}/revisions/${revisionId}/preview-token`, { method: "POST" }),
  archiveUrl: (id: string, revisionId: string) => `${BASE}/canvases/${id}/revisions/${revisionId}/archive.zip`,
};

// ---------- here.now publishing ----------

/**
 * Fired after any publication is created, updated, claimed, deleted or
 * forgotten, so the chat timeline and Published Sites view stay current
 * without polling. Every publication mutation goes through publishApi.
 */
export const PUBLICATIONS_CHANGED_EVENT = "chatclient:publications-changed";

function emitPublicationsChanged(publication?: SitePublication) {
  window.dispatchEvent(new CustomEvent(PUBLICATIONS_CHANGED_EVENT, { detail: publication ?? null }));
}

async function publicationMutation(promise: Promise<SitePublication>) {
  const publication = await promise;
  emitPublicationsChanged(publication);
  return publication;
}

export const publishApi = {
  connection: () => request<HereNowConnection>(`/integrations/herenow`),
  requestCode: (email: string) =>
    request<{ ok: boolean }>(`/integrations/herenow/request-code`, { method: "POST", body: JSON.stringify({ email }) }),
  verifyCode: (email: string, code: string) =>
    request<HereNowConnection>(`/integrations/herenow/verify-code`, { method: "POST", body: JSON.stringify({ email, code }) }),
  connectKey: (api_key: string) =>
    request<HereNowConnection>(`/integrations/herenow/key`, { method: "PUT", body: JSON.stringify({ api_key }) }),
  disconnect: () => request<{ ok: boolean }>(`/integrations/herenow`, { method: "DELETE" }),
  targets: () => request<PublishTarget[]>(`/integrations/herenow/targets`),
  list: (params: { chat_id?: string; workspace_id?: string } = {}) =>
    request<SitePublication[]>(`/publications${qs(params)}`),
  publish: (workspaceId: string, revisionId: string, body: PublishRequest) =>
    publicationMutation(request<SitePublication>(`/canvases/${workspaceId}/revisions/${revisionId}/publish`, { method: "POST", body: JSON.stringify(body) })),
  republish: (id: string) => publicationMutation(request<SitePublication>(`/publications/${id}/republish`, { method: "POST" })),
  claim: (id: string) => publicationMutation(request<SitePublication>(`/publications/${id}/claim`, { method: "POST" })),
  deleteSite: (id: string) => publicationMutation(request<SitePublication>(`/publications/${id}/delete-site`, { method: "POST" })),
  forget: async (id: string) => {
    await request<{ ok: boolean }>(`/publications/${id}`, { method: "DELETE" });
    emitPublicationsChanged();
  },
};

// ---------- Answer pipeline (Task 5.7) ----------
export const answerApi = {
  stream: async function* (
    chat_id: string,
    content: string,
    signal?: AbortSignal
  ): AsyncGenerator<AnswerSSEEvent> {
    const requestId = debugCapture.newRequestId();
    const res = await authedFetch(`${BASE}/answer`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Request-ID": requestId },
      body: JSON.stringify({ chat_id, content }),
      signal,
    });
    if (!res.ok || !res.body) {
      let msg = `HTTP ${res.status}`;
      try {
        const err = await res.json();
        msg = err.detail || msg;
      } catch {
        // ignore
      }
      const evt: AnswerSSEEvent = { type: "error", provider: null, model: null, retryable: false, message: msg };
      debugCapture.recordSSEFrame(requestId, evt, chat_id);
      yield evt;
      return;
    }
    for await (const evt of parseAnswerStream<AnswerSSEEvent>(res.body, signal)) {
      debugCapture.recordSSEFrame(requestId, evt, chat_id);
      yield evt;
    }
  },
};

// ---------- Search ----------
// F1 fix: every /api/search* route returns an envelope ({type?, results:
// [...]}), never a bare array — unwrap it here so every call site gets
// SearchResult[] like the type already promised.
export const searchApi = {
  search: (q: string, chat_id?: string, limit = 20) =>
    request<{ type: string; results: SearchResult[] }>(`/search${qs({ q, chat_id, limit })}`).then((r) => r.results),
  semantic: (q: string, chat_id?: string, limit = 20) =>
    request<{ results: SearchResult[] }>(`/search/semantic${qs({ q, chat_id, limit })}`).then((r) => r.results),
  fts: (q: string, chat_id?: string, limit = 20) =>
    request<{ results: SearchResult[] }>(`/search/fts${qs({ q, chat_id, limit })}`).then((r) => r.results),
  settings: () => request<SearchSettings>(`/search/settings`),
  /** `providers` order IS priority — the list order the router chain walks
   * (search-overhaul spec §4). Only `name`/`enabled` are sent; `available`
   * is server-computed and ignored on write. */
  saveSettings: (providers: { name: SearchProviderName; enabled: boolean }[]) =>
    request<SearchSettings>(`/search/settings`, { method: "PUT", body: JSON.stringify({ providers }) }),
};

// ---------- Search threads (per-mode ranked provider threads) ----------
export const searchThreadsApi = {
  get: (mode: SettingsMode) => request<SearchThreadsResponse>(`/search/threads${qs({ mode })}`),
  save: (mode: SettingsMode, threads: Omit<SearchThread, "id">[], min_results: number) =>
    request<SearchThreadsResponse>(`/search/threads${qs({ mode })}`, {
      method: "PUT",
      body: JSON.stringify({ threads, min_results }),
    }),
};

// ---------- Memory ----------
export const memoryApi = {
  list: (category?: string, limit = 100) =>
    request<Memory[]>(`/memory${qs({ category, limit })}`),
  add: (data: { content: string; category?: string; importance?: number; source_chat_id?: string }) =>
    request<Memory>(`/memory`, { method: "POST", body: JSON.stringify(data) }),
  /** Phase M — edit-in-place. The backend only re-embeds `content` when it
   * actually changes, so a category/importance-only edit is cheap. */
  update: (id: string, data: { content?: string; category?: string; importance?: number }) =>
    request<Memory>(`/memory/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  remove: (id: string) =>
    request<void>(`/memory/${id}`, { method: "DELETE" }),
  extract: (data: { content: string; chat_id?: string }) =>
    request<{ memories?: Memory[] }>(`/memory/extract`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
};

// ---------- Projects ----------
export const projectApi = {
  list: () => request<Project[]>(`/projects`),
  get: (id: string) => request<Project>(`/projects/${id}`),
  create: (data: { name: string; description?: string; instructions?: string }) =>
    request<Project>(`/projects`, { method: "POST", body: JSON.stringify(data) }),
  update: (id: string, data: Partial<Project>) =>
    request<Project>(`/projects/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  remove: (id: string) =>
    request<void>(`/projects/${id}`, { method: "DELETE" }),
  files: (id: string) => request<ProjectFile[]>(`/projects/${id}/files`),
  uploadFile: (id: string, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return request<ProjectFile>(`/projects/${id}/files`, {
      method: "POST",
      body: fd,
    });
  },
  fileContent: (id: string, fileId: string) =>
    request<{ filename: string; content: string; file_type: string }>(
      `/projects/${id}/files/${fileId}`
    ),
  removeFile: (id: string, fileId: string) =>
    request<void>(`/projects/${id}/files/${fileId}`, { method: "DELETE" }),
};

// ---------- Wiki ----------
export const wikiApi = {
  list: (projectId: string) =>
    request<WikiPage[]>(`/projects/${projectId}/wiki`),
  get: (projectId: string, pageId: string) =>
    request<WikiPage>(`/projects/${projectId}/wiki/${pageId}`),
  create: (projectId: string, data: { title: string; content: string }) =>
    request<WikiPage>(`/projects/${projectId}/wiki`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  update: (projectId: string, pageId: string, data: { title?: string; content?: string }) =>
    request<WikiPage>(`/projects/${projectId}/wiki/${pageId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  remove: (projectId: string, pageId: string) =>
    request<void>(`/projects/${projectId}/wiki/${pageId}`, { method: "DELETE" }),
  lint: (projectId: string, pageId: string) =>
    request<{ issues: WikiIssue[]; score: number; suggestions: string[] }>(
      `/projects/${projectId}/wiki/${pageId}/lint`,
      { method: "POST" }
    ),
  /** mode="project" drafts/updates from project instructions+files;
   * mode="chat" summarizes `chat_id` (ownership-checked) into a page.
   * `page_id` updates that page in place instead of creating a new one. */
  generate: (
    projectId: string,
    data: { mode: "project" | "chat"; chat_id?: string; page_id?: string }
  ) =>
    request<WikiPage>(`/projects/${projectId}/wiki/generate`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  /** Same same-origin-cookie download pattern as canvasApi.archiveUrl —
   * used as an <a href>, not fetched through request(). */
  exportUrl: (projectId: string) => `${BASE}/projects/${projectId}/wiki/export`,
  exportPageUrl: (projectId: string, pageId: string) =>
    `${BASE}/projects/${projectId}/wiki/${pageId}/export`,
};

// ---------- MCP ----------
// Task 6.3/6.6 — server_type is "catalog" (spawned by the mcp-runner
// sidecar, addressed by catalog_id) or "remote" (any https:// URL). "local"
// (backend-side stdio spawn) no longer exists — see mcp_service.py.
export const mcpApi = {
  list: () => request<MCPServer[]>(`/mcp/servers`),
  catalog: () => request<MCPCatalogEntry[]>(`/mcp/catalog`),
  reloadCatalog: () => request<{ reloaded: boolean; count: number }>(`/mcp/catalog/reload`, { method: "POST" }),
  create: (data: {
    name: string;
    server_type: "catalog" | "remote";
    catalog_id?: string;
    url?: string;
    requires_confirmation?: boolean;
    env_vars?: Record<string, string>;
  }) =>
    request<MCPServer>(`/mcp/servers`, { method: "POST", body: JSON.stringify(data) }),
  remove: (id: string) =>
    request<void>(`/mcp/servers/${id}`, { method: "DELETE" }),
  start: (id: string) =>
    request<{ started: boolean; status: string }>(`/mcp/servers/${id}/start`, { method: "POST" }),
  stop: (id: string) =>
    request<{ stopped: boolean }>(`/mcp/servers/${id}/stop`, { method: "POST" }),
  tools: (id: string) =>
    request<{ tools: MCPTool[] }>(`/mcp/servers/${id}/tools`).then((r) => r.tools),
};

// ---------- Skills ----------
export const skillsApi = {
  list: () => request<{ skills: Skill[] }>(`/skills`).then((r) => r.skills),
  get: (name: string) =>
    request<{ name: string; content: string }>(`/skills/${encodeURIComponent(name)}`),
  create: (data: { name: string; content: string }) =>
    request<Skill>(`/skills`, { method: "POST", body: JSON.stringify(data) }),
  update: (name: string, data: { content?: string }) =>
    request<Skill>(`/skills/${encodeURIComponent(name)}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  remove: (name: string) =>
    request<void>(`/skills/${encodeURIComponent(name)}`, { method: "DELETE" }),
  installUrl: (url: string, name?: string) =>
    request<Skill>(`/skills/install-url`, {
      method: "POST",
      body: JSON.stringify({ url, ...(name ? { name } : {}) }),
    }),
  installUpload: (file: File, name?: string) => {
    const body = new FormData();
    body.append("file", file);
    if (name) body.append("name", name);
    return request<Skill>(`/skills/install-upload`, { method: "POST", body });
  },
};

// ---------- Plugins ----------
export const pluginsApi = {
  list: () => request<Plugin[]>(`/plugins`),
  create: (data: { name: string; description?: string; manifest?: Record<string, unknown> }) =>
    request<Plugin>(`/plugins`, { method: "POST", body: JSON.stringify(data) }),
  update: (id: string, data: { name?: string; description?: string; manifest?: Record<string, unknown>; enabled?: boolean }) =>
    request<Plugin>(`/plugins/${id}`, { method: "PUT", body: JSON.stringify(data) }),
  remove: (id: string) =>
    request<void>(`/plugins/${id}`, { method: "DELETE" }),
  sync: () => request<{ synced?: number }>(`/plugins/sync`, { method: "POST" }),
  installRepo: (repo_url: string) =>
    request<Plugin>(`/plugins/install-repo`, {
      method: "POST",
      body: JSON.stringify({ repo_url }),
    }),
  installUpload: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return request<Plugin>(`/plugins/install-upload`, { method: "POST", body: fd });
  },
  enable: (id: string) => request<Plugin>(`/plugins/${id}/enable`, { method: "POST" }),
  disable: (id: string) => request<Plugin>(`/plugins/${id}/disable`, { method: "POST" }),
  // Task 7.9 (P8) — flattened slash-command list from enabled plugins, for
  // the composer's `/` autocomplete. No backend execution path: selecting
  // one inserts `prompt` as the user's own message text.
  commands: () =>
    request<{ name: string; description: string; prompt: string; plugin: string }[]>(
      `/plugins/commands`
    ),
  // Task 9.2 — the allowlisted repo hosts (Task 7.2's
  // PLUGIN_REPO_ALLOWLIST), for the "Install from repo URL" field's hint +
  // disabled state.
  allowlist: () => request<{ hosts: string[] }>(`/plugins/allowlist`).then((r) => r.hosts),
};

// ---------- Images ----------
export const imagesApi = {
  generate: (data: { prompt: string; width?: number; height?: number; chat_id?: string }) =>
    request<GeneratedImage>(`/images/generate`, {
      method: "POST",
      body: JSON.stringify(data),
    }),
  list: (chat_id?: string) =>
    request<GeneratedImage[]>(`/images${qs({ chat_id })}`),
  delete: (id: string) =>
    request<{ deleted: boolean }>(`/images/${id}`, { method: "DELETE" }),
};

export function imageUrl(path: string): string {
  // file_path is like "/data/images/xxx.png" or "uploads/xxx.png".
  // Task 8.5: must go through /api/uploads/{filename} — that's the only
  // route that's actually mounted (no bare /uploads/ StaticFiles exists),
  // and it's the one nginx's /api/ proxy covers with the auth cookie.
  const norm = path.replace(/^.*uploads\//, "").replace(/^\//, "");
  return `/api/uploads/${norm}`;
}

// ---------- Vision ----------
export const visionApi = {
  analyze: (image: File, prompt: string) => {
    const fd = new FormData();
    fd.append("file", image);
    fd.append("prompt", prompt);
    return request<{ analysis: string; file_path: string }>(`/vision/analyze`, {
      method: "POST",
      body: fd,
    });
  },
};

// ---------- Research ----------
export const researchApi = {
  start: (data: { topic: string; config?: Record<string, unknown> }) =>
    request<ResearchRun>(`/research`, { method: "POST", body: JSON.stringify(data) }),
  list: () => request<ResearchRun[]>(`/research`),
  get: (id: string) => request<ResearchRun>(`/research/${id}`),
  /** Task R1 — cancels a pending/running run, keeping whatever partial
   * `result` had already been persisted. A no-op 200 on an already-terminal
   * run (see research_service.cancel_research's docstring). */
  cancel: (id: string) => request<ResearchRun>(`/research/${id}/cancel`, { method: "POST" }),
};

// ---------- Places (Google Places-backed source cards) ----------
export const placesApi = {
  status: () => request<{ configured: boolean; browser_key: string | null }>(`/places/status`),
  /** Pure URL builder — the caller uses this as an <img src>, which rides
   * the session cookie same-origin, so bytes are never fetched manually. */
  photoUrl: (name: string, w = 160) => `${BASE}/places/photo${qs({ name, w })}`,
  /** points is "lat,lng;lat,lng;...". Static Maps may 404 if the API isn't
   * enabled on the operator's project (documented, expected) — the caller
   * must hide the image on error, not treat a 404 as a bug. */
  staticMapUrl: (points: string, opts: { w?: number; h?: number; theme?: "light" | "dark" } = {}) =>
    `${BASE}/places/staticmap${qs({ points, w: opts.w ?? 640, h: opts.h ?? 360, theme: opts.theme ?? "dark" })}`,
};

// ---------- LLM gateway / catalog (Task 4.6/4.4) ----------
// Replaces the old modelsApi (`/api/models`, `/api/models/vision` —
// deleted, they predate the provider layer and only ever listed direct
// Ollama models).
export const llmApi = {
  /** `refresh=true` is the ONLY thing that re-hits the gateway — never poll. */
  models: (refresh = false) => request<ModelsByProvider>(`/llm/models${qs({ refresh })}`),
  settings: () => request<LLMSettings>(`/llm/settings`),
  saveSettings: (data: Partial<LLMSettings>) =>
    request<LLMSettings>(`/llm/settings`, { method: "PUT", body: JSON.stringify(data) }),
};

// ---------- Admin: user management ----------
export interface AdminUser {
  id: string;
  email: string;
  role: "user" | "admin";
  email_verified: boolean;
  is_disabled: boolean;
  created_at: string;
}

export const adminApi = {
  listUsers: () => request<AdminUser[]>(`/admin/users`),
  disableUser: (id: string) => request<AdminUser>(`/admin/users/${id}/disable`, { method: "POST" }),
  enableUser: (id: string) => request<AdminUser>(`/admin/users/${id}/enable`, { method: "POST" }),
  deleteUser: (id: string) => request<{ deleted: boolean }>(`/admin/users/${id}`, { method: "DELETE" }),
};

// ---------- Diagnostics (Phase D4) ----------
export const debugApi = {
  status: () => request<{ capture_enabled: boolean }>(`/debug/status`),
  chatCapture: (chatId: string) =>
    request<{
      chat: Record<string, unknown>;
      messages: Record<string, unknown>[];
      events: Record<string, unknown>[];
      buffer_stats: { count: number; capacity: number };
    }>(`/chats/${chatId}/debug-capture`),
  saveChatCapture: (chatId: string, frontend: unknown) =>
    request<{ filename: string; path: string; bytes: number }>(
      `/chats/${chatId}/debug-capture/save`,
      { method: "POST", body: JSON.stringify({ frontend }) }
    ),
};

export { ApiError };
